/**
 * DIS-1914: whether the app knows a member is signed in.
 * - `pending`: the app is still working this out, so a screen must not show "sign in" yet.
 * - `valid`: settled and signed in.
 * - `none`: settled and signed out.
 *
 * This sits beside `userStore.isLoggedIn` and does not change what it means.
 */
export type SessionState = 'pending' | 'valid' | 'none';

export type SessionStateInputs = {
  isAppInitialising: boolean;
  isLoggedIn: boolean;
};

export const computeSessionState = (i: SessionStateInputs): SessionState => {
  // Init restores the session on both platforms before isAppInitialising clears
  // (rootStore.initialise), so after init isLoggedIn is settled. A later logout
  // clears isSessionChecked on web; that is signed out, not unknown. The
  // foreground probe is not folded in here: the offer screen reads it as a
  // separate input, so a probe cannot replace a screen that has settled.
  if (i.isAppInitialising) return 'pending';
  return i.isLoggedIn ? 'valid' : 'none';
};

export type SessionProbeResult = 'dead' | 'unclassified' | 'unknown';

/**
 * DIS-1914: what a failed GET /ecommerce/session probe says about the session.
 * - `dead`: a definitive 401, so the caller ends the session.
 * - `unclassified`: a 401 `{valid:false}` on native. A native client on the
 *   legacy auth path gets this answer for a live session (spec risk R-1), so
 *   the caller logs it and changes nothing.
 * - `unknown`: offline, timeout, 5xx or any other answer. The caller keeps the session.
 */
export const classifySessionProbeError = (i: {
  status?: number;
  body?: {recovery?: string; valid?: boolean};
  platform: 'web' | 'native';
}): SessionProbeResult => {
  if (i.status !== 401) return 'unknown';
  if (i.body?.recovery === 'reauth') return 'dead';
  if (i.body?.valid === false) return i.platform === 'web' ? 'dead' : 'unclassified';
  return 'unknown';
};
